import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { DATE_RANGE_PRESETS } from "../lib/dateRangePresets.js";
import {
  formatDateRange,
  formatDateRangeFull,
  formatDateRangeCompact,
  isCustomRange,
  getRangeBounds,
  makeCustomRange,
  earliestRangeStart,
  MAX_RANGE_DAYS,
} from "../lib/shell.js";
import { NOTE_CLASS } from "../lib/ui.js";
import { phAttr } from "../lib/analytics/attrs.js";
import { ACTION, ATTRIBUTE } from "../lib/analytics/surfaceVocabulary.js";

export function ThemeToggle({ theme, onToggle, children }) {
  return (
    <button
      className="grid h-[28px] w-[28px] place-items-center border border-[color:var(--line)] bg-[color:var(--control-bg)] p-0 text-[color:var(--muted)] [&_svg]:block [&_svg]:h-3 [&_svg]:w-3"
      type="button"
      aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
      onClick={onToggle}
    >
      {children}
    </button>
  );
}

function toInputDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export default function DateRangePicker({ range, onRangeChange }) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  // Menu position is computed from the trigger's bounding rect and
  // applied as inline `position: fixed` on the portal'd menu. The
  // sidebar topbar this picker lives in has `overflow: hidden` for
  // its collapse animation, and ancestor transforms get applied
  // during sidebar collapse/expand — both would clip or relocate a
  // naively-`absolute` dropdown. Portalling to <body> + fixed
  // coords sidesteps both.
  const [menuPos, setMenuPos] = useState({ left: 0, top: 0, minWidth: 0 });

  const custom = isCustomRange(range);
  const bounds = getRangeBounds(range);

  const recompute = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    setMenuPos({ left: r.left, top: r.bottom + 4, minWidth: r.width });
  }, []);

  // Snap into position synchronously so the first paint shows the
  // menu in the right spot — otherwise the user briefly sees a
  // flash at the previous coordinates.
  useLayoutEffect(() => {
    if (open) recompute();
  }, [open, recompute]);

  useEffect(() => {
    if (!open) return;
    const onScrollResize = () => recompute();
    const onClickOutside = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    window.addEventListener("mousedown", onClickOutside);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
      window.removeEventListener("mousedown", onClickOutside);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, recompute]);

  const applyCustom = (startStr, endStr) => {
    const s = new Date(startStr + "T00:00:00");
    const e = new Date(endStr + "T00:00:00");
    if (!isNaN(s) && !isNaN(e) && s <= e) {
      onRangeChange(makeCustomRange(s, e));
    }
  };

  const selectPreset = (id) => {
    onRangeChange(id);
    setOpen(false);
  };

  return (
    <div data-date-picker className="relative grow-1 @container">
      <button
        ref={triggerRef}
        type="button"
        data-date-trigger
        data-ph-capture-attribute-action={phAttr(ATTRIBUTE.ACTION, ACTION.RANGE_PICKER)}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[38px] w-full list-none items-center gap-2 border-x border-t border-[color:var(--line)] bg-[color:var(--control-bg)] px-2 text-left"
      >
        <strong className="min-w-0 flex-1 truncate text-[12px] leading-[1.08] font-medium">
          <span className="@[180px]:hidden">{formatDateRangeCompact(range)}</span>
          <span className="hidden @[180px]:inline @[260px]:hidden">{formatDateRange(range)}</span>
          <span className="hidden @[260px]:inline">{formatDateRangeFull(range)}</span>
        </strong>
        <span
          data-date-caret
          className={`grid h-3 w-3 shrink-0 place-items-center text-[color:var(--muted)] transition-transform [&_svg]:block [&_svg]:h-[10px] [&_svg]:w-[10px] ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 6.5 8 10 12 6.5" />
          </svg>
        </span>
      </button>

      {open ? createPortal(
        <div
          ref={menuRef}
          data-date-menu
          style={{
            position: "fixed",
            left: menuPos.left,
            top: menuPos.top,
            minWidth: Math.max(250, menuPos.minWidth),
          }}
          className="z-[1000] grid border border-[color:var(--line)] bg-[color:var(--panel)] shadow-lg"
        >
          {DATE_RANGE_PRESETS.map((item, index) => {
            const isActive = !custom && item.id === range;
            return (
            <button
              key={item.id}
              type="button"
              className={[
                "grid gap-[3px] border-0 border-t border-[color:var(--line)] bg-transparent px-[10px] py-2 text-left cursor-pointer transition-colors",
                index === 0 ? "border-t-0" : "",
                isActive
                  ? "relative !bg-[color:var(--green-soft)] before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[color:var(--green)]"
                  : "hover:bg-[color:var(--control-bg)]",
              ].filter(Boolean).join(" ")}
              data-active={isActive}
              onClick={() => selectPreset(item.id)}
            >
              <div className="grid [grid-template-columns:minmax(0,1fr)_auto] items-start gap-x-[10px]">
                <strong className="text-[12px] leading-[1.12] font-medium">{item.summary}</strong>
                <span className="text-[11px] leading-[1.08] text-[color:var(--muted)] uppercase tracking-[0.06em]">
                  {item.label}
                </span>
              </div>
              <div className={NOTE_CLASS}>{formatDateRange(item.id)}</div>
            </button>
            );
          })}

          <div
            className={[
              "grid gap-[6px] border-t border-[color:var(--line)] px-[10px] py-2",
              custom
                ? "relative bg-[color:var(--green-soft)] before:absolute before:inset-y-0 before:left-0 before:w-[2px] before:bg-[color:var(--green)]"
                : "",
            ].filter(Boolean).join(" ")}
          >
            <strong className="text-[12px] leading-[1.12] font-medium">Custom range</strong>
            <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-[6px]">
              <input
                type="date"
                data-date-input
                className="min-w-0 border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[3px] text-[11px] leading-[1.2] text-[color:var(--ink)]"
                value={toInputDate(bounds.startDate)}
                min={toInputDate(earliestRangeStart(bounds.endDate))}
                max={toInputDate(bounds.endDate)}
                onChange={(e) => applyCustom(e.target.value, toInputDate(bounds.endDate))}
              />
              <span className="text-[11px] text-[color:var(--muted)]">–</span>
              <input
                type="date"
                data-date-input
                className="min-w-0 border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[6px] py-[3px] text-[11px] leading-[1.2] text-[color:var(--ink)]"
                value={toInputDate(bounds.endDate)}
                min={toInputDate(bounds.startDate)}
                onChange={(e) => applyCustom(toInputDate(bounds.startDate), e.target.value)}
              />
            </div>
            <div className={NOTE_CLASS} data-date-range-limit>Up to {MAX_RANGE_DAYS} days at a time.</div>
          </div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
